import React, { useRef, useState } from 'react';
import { ArrowLeft, Camera, ImagePlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import { Button } from 'components/common/Button';
import { Card } from 'components/common/Card';
import { CostDocumentService } from 'services/costs/cost-document.service';

export default function ReceiptCapturePage() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [captureSource, setCaptureSource] = useState<'WEB_UPLOAD' | 'WEB_CAMERA'>('WEB_UPLOAD');
  const [documentType, setDocumentType] = useState<'RECEIPT' | 'INVOICE' | 'UNKNOWN'>('UNKNOWN');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, 10 - files.length);
    setFiles(current => [...current, ...selected]);
    event.target.value = '';
  };

  const openPicker = (source: 'WEB_UPLOAD' | 'WEB_CAMERA') => {
    setCaptureSource(source);
    (source === 'WEB_CAMERA' ? cameraInputRef : uploadInputRef).current?.click();
  };

  const processReceipt = async () => {
    if (!org?.id || !currentStore?.id || !files.length) return;
    setProcessing(true);
    setError(null);
    try {
      const document = await CostDocumentService.uploadReceipt(org.id, currentStore.id, files, captureSource, documentType);
      navigate(`/costs/processing/${document.id}`);
    } catch (uploadError: any) {
      setError(uploadError?.message ?? 'Unable to process receipt.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Button variant="ghost" size="sm" onClick={() => navigate('/costs')}><ArrowLeft size={14} /> Costs</Button>
      <div>
        <h1 className="text-xl font-bold text-gray-900">Capture Receipt</h1>
        <p className="mt-1 text-sm text-gray-500">Take a clear photo of the invoice or receipt.</p>
      </div>
      <Card>
        <label className="mb-4 block max-w-xs text-sm text-gray-700">Document type<select value={documentType} onChange={event => setDocumentType(event.target.value as 'RECEIPT' | 'INVOICE' | 'UNKNOWN')} className="mt-1 w-full rounded border border-gray-300 px-3 py-2"><option value="UNKNOWN">Receipt or invoice</option><option value="RECEIPT">Receipt</option><option value="INVOICE">Invoice</option></select></label>
        <input ref={uploadInputRef} type="file" accept="application/pdf,image/jpeg,image/png" multiple className="hidden" onChange={addFiles} />
        <input ref={cameraInputRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={addFiles} />
        {!files.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Button size="lg" className="order-2 sm:order-1" variant="secondary" onClick={() => openPicker('WEB_UPLOAD')}><ImagePlus size={18} /> Upload Receipt</Button>
            <Button size="lg" className="order-1 sm:order-2" onClick={() => openPicker('WEB_CAMERA')}><Camera size={18} /> Take Photo</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {files.map((file, index) => (
                <div key={`${file.name}-${index}`} className="relative overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                  {file.type.startsWith('image/') ? <img src={URL.createObjectURL(file)} alt={`Receipt page ${index + 1}`} className="aspect-square w-full object-cover" /> : <div className="flex aspect-square items-center justify-center p-3 text-center text-xs text-gray-500">{file.name}</div>}
                  <button type="button" aria-label={`Remove photo ${index + 1}`} onClick={() => setFiles(current => current.filter((_, itemIndex) => itemIndex !== index))} className="absolute right-1 top-1 rounded-full bg-white p-1 text-gray-600 shadow"><X size={14} /></button>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500">{files.length} photo{files.length === 1 ? '' : 's'} selected</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => openPicker('WEB_CAMERA')} disabled={files.length >= 10}><ImagePlus size={15} /> Add Photo</Button>
              <Button variant="secondary" onClick={() => setFiles([])}>Retake</Button>
              <Button loading={processing} onClick={processReceipt}>Process Receipt</Button>
            </div>
          </div>
        )}
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </Card>
      <p className="text-xs text-gray-400">Keep the entire receipt visible and avoid glare or shadows. Long receipts can use additional photos.</p>
    </div>
  );
}
